import { useLoaderData } from 'react-router';
import { FIVE_G_PLUS_SPEED_THRESHOLD_MBPS } from '../../common/constants/subscriptionConstants.js';
import { OfferType } from '../../common/enums.js';
import { productOfferingsEmptyMessage, productOfferingsEmptyTitle, t } from '../../common/i18n/index.js';
import { getDownloadSpeedInMbps } from '../../common/utils/productOfferingsUtils.js';
import { ProductOfferingsEmptyPageCoCo } from '../EmptyOrError/ProductOfferingsEmptyPageCoCo.js';
import { Grid } from '../Grid/Grid.js';
import { MobileCarouselWrapper } from '../HorizontalSubscriptionCard/SubscriptionUtils.js';
import { ProductOfferingsCardCoCo } from './ProductOfferingsCardCoCo.js';
import type { ProductOfferingsResponse } from '../../generated/api/productOfferingsResponse.js';

export interface HorizontalVoiceProductOfferingsCoCoProps {
  publicPages: boolean;
}

const sortBySpeedDescending = (productOfferings: ProductOfferingsResponse[]): ProductOfferingsResponse[] =>
  [...productOfferings].sort((a, b) => getDownloadSpeedInMbps(b) - getDownloadSpeedInMbps(a));

const groupProductOfferingsBySpeed = (productOfferings: ProductOfferingsResponse[]) => {
  const fiveGPlus: ProductOfferingsResponse[] = [];
  const fiveG: ProductOfferingsResponse[] = [];

  productOfferings.forEach(sub => {
    const speedMbps = getDownloadSpeedInMbps(sub);
    (speedMbps > FIVE_G_PLUS_SPEED_THRESHOLD_MBPS ? fiveGPlus : fiveG).push(sub);
  });

  return {
    fiveGPlus: sortBySpeedDescending(fiveGPlus),
    fiveG: sortBySpeedDescending(fiveG),
  };
};

interface SubscriptionGroupProps {
  title: string;
  offerings: ProductOfferingsResponse[];
  publicPages: boolean;
}

const SubscriptionGroup = ({ title, offerings, publicPages }: SubscriptionGroupProps) =>
  offerings.length > 0 ? (
    <>
      <h2 className="of-subscriptions-main-container-carousel-title">{title}</h2>
      <MobileCarouselWrapper>
        {offerings.map(sub => (
          <ProductOfferingsCardCoCo
            key={sub.id}
            productOfferings={sub}
            publicPages={publicPages}
            offerType={OfferType.VOICE}
          />
        ))}
      </MobileCarouselWrapper>
    </>
  ) : null;

export const HorizontalVoiceProductOfferingsCoCo = ({ publicPages }: HorizontalVoiceProductOfferingsCoCoProps) => {
  const productOfferings = (useLoaderData() as ProductOfferingsResponse[]) ?? [];

  if (productOfferings.length === 0) {
    return (
      <Grid colWidthL={publicPages ? 10 : 12}>
        <ProductOfferingsEmptyPageCoCo
          title={t.ZZWO(productOfferingsEmptyTitle)}
          message={t.ZZWM(productOfferingsEmptyMessage)}
        />
      </Grid>
    );
  }

  const { fiveGPlus, fiveG } = groupProductOfferingsBySpeed(productOfferings);

  return (
    <Grid colWidthL={publicPages ? 10 : 12}>
      <div className="of-subscriptions-main-container of-subscription-card-container-coco">
        <SubscriptionGroup
          title={t.Y6ZI('Comprehensive 5G+ subscriptions')}
          offerings={fiveGPlus}
          publicPages={publicPages}
        />
        <SubscriptionGroup title={t.PN6W('5G subscriptions')} offerings={fiveG} publicPages={publicPages} />
      </div>
    </Grid>
  );
};
